import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ConfirmDialog } from '@saas-kit/ui';

import type { AdminUser } from './api';

type ActionKey = 'suspend' | 'reactivate';

interface Props {
  user: AdminUser;
  pendingAction: ActionKey | null;
  onSuspend: (user: AdminUser) => void;
  onReactivate: (user: AdminUser) => void;
}

export function UserActions({
  user,
  pendingAction,
  onSuspend,
  onReactivate,
}: Props): JSX.Element {
  const { t } = useTranslation('adminUsers');
  const [confirm, setConfirm] = useState<ActionKey | null>(null);

  const isPending = pendingAction !== null;

  function handleConfirm(): void {
    if (!confirm) return;
    if (confirm === 'suspend') onSuspend(user);
    if (confirm === 'reactivate') onReactivate(user);
    setConfirm(null);
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {user.is_active ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirm('suspend')}
            disabled={isPending}
          >
            {t('actions.suspend')}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirm('reactivate')}
            disabled={isPending}
          >
            {t('actions.reactivate')}
          </Button>
        )}
      </div>

      {confirm ? (
        <ConfirmDialog
          title={t(`confirm.${confirm}.title`, { email: user.email })}
          body={t(`confirm.${confirm}.body`, { email: user.email })}
          confirmLabel={t(`actions.${confirm}`)}
          variant={confirm === 'suspend' ? 'destructive' : 'primary'}
          isBusy={pendingAction === confirm}
          onCancel={() => setConfirm(null)}
          onConfirm={handleConfirm}
        />
      ) : null}
    </>
  );
}
